import React, { useState } from "react";
import {
  FiX,
  FiAlertCircle,
  FiImage,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";

const ViewP2PSellOrderModal = ({
  isOpen,
  onClose,
  transaction,
  fetchOrders,
}) => {
  // Modal states for Sell Actions
  const [actionModalType, setActionModalType] = useState(null);
  const [hashInput, setHashInput] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !transaction) return null;

  const getStatusStyle = (statusVal) => {
    switch (statusVal) {
      case "approve":
        return "bg-emerald-100 text-emerald-600 border-emerald-200";
      case "reject":
        return "bg-red-100 text-red-600 border-red-200";
      case "requested":
        return "bg-blue-100 text-blue-600 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-600 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const closeActionModal = () => {
    setActionModalType(null);
    setHashInput("");
    setRejectReason("");
  };

  const handleSubmitAction = async (e) => {
    e.preventDefault();

    // if (actionModalType === "approve" && !hashInput.trim()) {
    //   return showErrorToast("Transaction Hash (UTR) is required for approval.");
    // }
    if (actionModalType === "reject" && !rejectReason.trim()) {
      return showErrorToast("Please provide a reason for rejection.");
    }

    try {
      setIsSubmitting(true);

      const payload = {
        orderId: transaction?._id ?? "NA",
        status: actionModalType,
      };

      if (actionModalType === "reject") {
        payload.rejectReason = rejectReason;
      }

      const response = await makeRequestAuth(
        "post",
        "/admin/releaseAmountRejectOrApprove",
        payload,
      );

      if (
        response.status === true ||
        response.success === true ||
        response.data
      ) {
        showSuccessToast(`Order ${actionModalType}d successfully!`);
        closeActionModal();
        if (fetchOrders) fetchOrders();
        onClose();
      } else {
        showErrorToast(
          response.message || `Failed to ${actionModalType} order`,
        );
      }
    } catch (err) {
      console.error(`Submit ${actionModalType} Error:`, err);
      showErrorToast(
        err.response?.data?.message || err.message || "Server error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#1E2738] rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col border border-purple-300 dark:border-purple-600/50 animate-fadeIn max-h-[90vh]">
          {/* Main Modal Header */}
          <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-2xl">
            <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Transaction Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-red-500 transition p-1"
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Main Modal Body */}
          <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
            {/* Order Summary */}
            <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Order Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Order ID
                  </p>
                  <p className="font-mono text-sm font-medium text-gray-900 dark:text-white truncate">
                    {transaction?.orderId ?? "NA"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Type
                  </p>
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded ${transaction.txType === "BUY" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}
                  >
                    {transaction.txType}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Amount ({transaction.walletType})
                  </p>
                  <p className="font-bold text-gray-900 dark:text-white text-base">
                    {transaction.amount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Rate / Status
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 dark:text-white">
                      ₹{transaction.rate}
                    </span>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${getStatusStyle(transaction.status)}`}
                    >
                      {transaction.status}
                    </span>
                  </div>
                </div>
              </div>

              {transaction.hash && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Transaction UTR
                  </p>
                  <p className="font-mono text-sm font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-700/50 p-2 rounded-lg border border-gray-100 dark:border-gray-600 break-all">
                    {transaction.hash}
                  </p>
                </div>
              )}
            </div>

            {/* Users Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Creator */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex justify-between">
                  <span>Creator</span>
                  <span className="text-xs text-purple-600 bg-purple-100 px-2 rounded-full flex items-center">
                    {transaction.creatorModel}
                  </span>
                </h3>
                {transaction.creator ? (
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-500">Name:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.creator.name}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Email:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.creator.email}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Mobile:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.creator.mobile}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Creator details not populated.
                  </p>
                )}
              </div>

              {/* Counterparty */}
              <div className="bg-gray-50 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  Counterparty
                </h3>
                {transaction.counterParty ? (
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-500">Name:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.counterParty.name}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Email:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.counterParty.email}
                      </span>
                    </p>
                    <p>
                      <span className="text-gray-500">Mobile:</span>{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {transaction.counterParty.mobile}
                      </span>
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center pb-6">
                    <p className="text-sm text-gray-500 italic">
                      No counterparty assigned yet.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Methods & Image */}
            {(transaction.paymentMethods?.length > 0 || transaction.image) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`flex flex-col gap-3 ${!transaction.image ? "md:col-span-2 grid sm:grid-cols-2" : ""}`}
                >
                  {transaction.paymentMethods?.length > 0 && (
                    <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-1">
                      Payment Methods
                    </h3>
                  )}
                  {transaction.paymentMethods?.map((method, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 relative w-full"
                    >
                      {method.isPrimary && (
                        <span className="absolute top-3 right-3 text-[10px] font-bold bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                          PRIMARY
                        </span>
                      )}
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                        {method.methodType}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        {method.label || "No Label"}
                      </p>
                      <div className="space-y-1.5 border-t border-gray-200 dark:border-gray-700 pt-3">
                        {Object.entries(method.details || {}).map(
                          ([key, value]) => (
                            <div key={key} className="flex flex-col">
                              <span className="text-[10px] text-gray-500 uppercase tracking-wider">
                                {key}
                              </span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white break-all">
                                {String(value)}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {transaction.image && (
                  <div className="flex flex-col h-full">
                    <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 px-1 flex items-center gap-2">
                      <FiImage /> Attached Receipt
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 flex-1 flex flex-col items-center justify-center relative group">
                      <a
                        href={transaction.image}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-full flex items-center justify-center"
                      >
                        <img
                          src={transaction.image}
                          alt="Receipt"
                          className="max-h-[200px] w-auto object-contain rounded-lg shadow-sm border border-gray-300 dark:border-gray-600 bg-white transition-opacity group-hover:opacity-80"
                        />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Modal Footer with conditional ACTION BUTTONS */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl flex justify-between items-center">
            {/* Left Side Actions (Approve/Reject for SELL orders that are requested) */}
            <div className="flex gap-3">
              {transaction.txType === "SELL" &&
                transaction.status === "requested" && (
                  <>
                    <button
                      onClick={() => setActionModalType("approve")}
                      className="px-5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition shadow-lg shadow-emerald-500/30 flex items-center gap-2"
                    >
                      <FiCheckCircle /> Approve
                    </button>
                    <button
                      onClick={() => setActionModalType("reject")}
                      className="px-5 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition shadow-lg shadow-red-500/30 flex items-center gap-2"
                    >
                      <FiXCircle /> Reject
                    </button>
                  </>
                )}
            </div>

            {/* Right Side Action (Close) */}
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* --- SECONDARY MODAL FOR APPROVE / REJECT --- */}
      {actionModalType && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-fadeIn">
          <div
            className={`bg-white dark:bg-[#1E2738] rounded-2xl shadow-2xl w-full max-w-lg border ${actionModalType === "approve" ? "border-emerald-300 dark:border-emerald-600/50" : "border-red-300 dark:border-red-600/50"}`}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white capitalize">
                {actionModalType} Transaction
              </h2>
              <button
                onClick={closeActionModal}
                className="text-gray-500 hover:text-red-500 transition p-1"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitAction} className="p-6 space-y-5">
              {/* Dynamic instruction text */}
              {/* <div
                className={`p-3 rounded-lg text-sm mb-2 ${actionModalType === "approve" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}
              >
                {actionModalType === "approve"
                  ? "Provide the UTR to approve this transaction."
                  : "Provide a valid reason for rejecting this transaction."}
              </div>

              {actionModalType === "approve" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Transaction UTR <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={hashInput}
                    onChange={(e) => setHashInput(e.target.value)}
                    placeholder="Enter UTR"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition"
                    required
                  />
                </div>
              )} */}

              {/* REJECT FIELDS */}
              {actionModalType === "reject" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows="3"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter reason..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-red-500 outline-none transition"
                    required
                  ></textarea>
                </div>
              )}

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={closeActionModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-white font-medium shadow-lg transition disabled:opacity-50 flex justify-center items-center gap-2 ${actionModalType === "approve" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"}`}
                >
                  {isSubmitting ? (
                    <Spinner className="w-5 h-5" />
                  ) : (
                    `Confirm ${actionModalType}`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewP2PSellOrderModal;
